import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useApi } from "./useApi";
import { apiErrorMessage } from "../utils/apiError";
import { useLatest } from "./useLatest";
import { EvidenceAttachmentRef, EvidenceSlot } from "../types/evidence";

export interface EvidenceAttachments {
  attachments: EvidenceAttachmentRef[];
  error: boolean;
  unknown: boolean;
  /** Keyed by the storage key of the attachment a removal failed on. */
  errors: Record<string, string>;
  /** Whether that one row is working: a row waits for its own upload, never for another row's. */
  busy: (key: string) => boolean;
  disabled: boolean;
  refetch: () => void;
  /** `productTitle` names the product a product_photo belongs to. Resolves to the failure message, null on success. */
  upload: (slot: EvidenceSlot, key: string, file: File, productTitle?: string) => Promise<string | null>;
  remove: (storageKey: string) => Promise<string | null>;
}

/** One fetch for the whole column: the needs-attention rows and the optional rows read the same list. */
export function useEvidenceAttachments(draftId: number | undefined, disabled: boolean, onChange?: () => void): EvidenceAttachments {
  const { get, postForm, del } = useApi();
  const [attachments, setAttachments] = useState<EvidenceAttachmentRef[]>([]);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busyKeys, setBusyKeys] = useState<string[]>([]);
  const setBusy = (key: string, running: boolean) => {
    setBusyKeys((prev) => (running ? [...prev, key] : prev.filter((busy) => busy !== key)));
  };

  // A ref, not an effect-scoped flag: it stays valid across the upload/remove awaits too.
  const draftIdRef = useLatest(draftId);

  // Reports whether the list on screen is the server's: a write that cannot say so would be
  // reported as done while the row it belongs to stays empty.
  const fetchAttachments = useCallback(async (): Promise<boolean> => {
    const requestedDraftId = draftId;
    if (!requestedDraftId) return false;
    try {
      const res = await get<{ attachments: EvidenceAttachmentRef[] }>(`/api/evidence-drafts/${requestedDraftId}/attachments`);
      if (draftIdRef.current !== requestedDraftId) return false;
      setAttachments(res.attachments);
      setLoaded(true);
      setError(false);
      return true;
    } catch (err) {
      Sentry.captureException(err);
      if (draftIdRef.current !== requestedDraftId) return false;
      setError(true);
      return false;
    }
  }, [get, draftId, draftIdRef]);

  // Cleared before the new draft's load runs: a failed one would otherwise leave the previous
  // draft's attachments on screen as this draft's known rows.
  useEffect(() => {
    setAttachments([]);
    setLoaded(false);
    setError(false);
  }, [draftId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const setRowError = (key: string, message: string | null) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  };

  const upload = async (slot: EvidenceSlot, key: string, file: File, productTitle?: string): Promise<string | null> => {
    const requestedDraftId = draftId;
    if (!requestedDraftId) return null;

    setBusy(key, true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("slot", slot);
      if (productTitle !== undefined) fd.append("productTitle", productTitle);
      await postForm(`/api/evidence-drafts/${requestedDraftId}/attachments`, fd, { slowApi: true });
      if (draftIdRef.current !== requestedDraftId) return null;
      const reloaded = await fetchAttachments();
      if (draftIdRef.current !== requestedDraftId) return null;
      onChange?.();
      // The file is on the server and the list on screen is not: closing as a success would leave
      // the row empty and the agent uploading the same file again.
      return reloaded ? null : "The file uploaded, but the screenshot list did not reload.";
    } catch (err: any) {
      Sentry.captureException(err);
      if (draftIdRef.current !== requestedDraftId) return null;
      return apiErrorMessage(err, "Failed to upload the screenshot");
    } finally {
      setBusy(key, false);
    }
  };

  const remove = async (storageKey: string): Promise<string | null> => {
    const requestedDraftId = draftId;
    if (!requestedDraftId) return null;
    setRowError(storageKey, null);
    setBusy(storageKey, true);
    try {
      await del(`/api/evidence-drafts/${requestedDraftId}/attachments?key=${encodeURIComponent(storageKey)}`);
      if (draftIdRef.current !== requestedDraftId) return null;
      const reloaded = await fetchAttachments();
      if (draftIdRef.current !== requestedDraftId) return null;
      onChange?.();
      if (reloaded) return null;
      const stale = "The attachment was removed, but the screenshot list did not reload.";
      setRowError(storageKey, stale);
      return stale;
    } catch (err: any) {
      Sentry.captureException(err);
      if (draftIdRef.current !== requestedDraftId) return null;
      const message = apiErrorMessage(err, "Failed to remove the attachment");
      setRowError(storageKey, message);
      return message;
    } finally {
      setBusy(storageKey, false);
    }
  };

  return {
    attachments,
    error,
    unknown: error && !loaded,
    errors,
    busy: (key: string) => busyKeys.includes(key),
    disabled: disabled || error,
    refetch: fetchAttachments,
    upload,
    remove,
  };
}
